/**
 * Locale-resolved display name of a REFERENCED object — its synonym in the
 * active language — for field chrome that would otherwise print the object's
 * raw configuration name.
 *
 * Two sources, in order:
 *   1. the entities registry the FK pickers already cache — ONE request covers
 *      every catalog and document on the connection;
 *   2. the object's own configuration — enums (and anything else the registry
 *      omits) are ordinary config objects and carry the same synonym fields.
 *
 * Both are cached and in-flight-deduped, so a form with many ref fields makes
 * one registry call plus at most one call per distinct non-registry target.
 * Any failure degrades to the raw name rather than blanking the chrome.
 */
import { useEffect, useState } from 'react'
import apiClient from '@/utils/api'
import { entitiesCache, entitiesCacheKeyForReferencePickers } from '@/utils/entitiesCache'
import { configObjectApi } from '../designer/services/configObjectApi'
import { resolveLabel } from './resolveLabel'

interface LabelSource {
  name?: string
  synonym?: string
  synonyms?: Record<string, string>
}

interface EntitiesPayload {
  catalogs?: LabelSource[]
  documents?: LabelSource[]
}

const entitiesInFlight = new Map<string, Promise<EntitiesPayload>>()

async function loadEntities(connectionId: string): Promise<EntitiesPayload> {
  const key = entitiesCacheKeyForReferencePickers(connectionId)
  const cached = entitiesCache.get(key)
  if (cached) return cached as EntitiesPayload

  let pending = entitiesInFlight.get(key)
  if (!pending) {
    pending = apiClient
      .get('/v1/designer/entities', {
        params: { connection_id: connectionId, include_excluded: true },
      })
      .then((res: any) => (res?.data ?? {}) as EntitiesPayload)
      .finally(() => entitiesInFlight.delete(key))
    entitiesInFlight.set(key, pending)
  }
  const entities = await pending
  // Only cache a payload that actually looks like the entities registry — this
  // cache is shared with the FK pickers, and poisoning it would break them too.
  if (Array.isArray(entities?.catalogs) || Array.isArray(entities?.documents)) {
    entitiesCache.set(key, entities)
  }
  return entities
}

/**
 * Config-object fallback. `null` is cached too: an unknown or unreadable name
 * must not re-request on every field mount.
 */
const configCache = new Map<string, LabelSource | null>()
const configInFlight = new Map<string, Promise<LabelSource | null>>()

async function loadObjectConfig(
  connectionId: string,
  objectName: string,
): Promise<LabelSource | null> {
  const key = `${connectionId}|${objectName}`
  if (configCache.has(key)) return configCache.get(key) ?? null

  let pending = configInFlight.get(key)
  if (!pending) {
    pending = configObjectApi
      .getObject(connectionId, objectName)
      .then((obj: any) =>
        obj && typeof obj === 'object'
          ? ({ name: obj.name, synonym: obj.synonym, synonyms: obj.synonyms } as LabelSource)
          : null,
      )
      .catch(() => null)
      .then((source) => {
        configCache.set(key, source)
        return source
      })
      .finally(() => configInFlight.delete(key))
    configInFlight.set(key, pending)
  }
  return pending
}

async function resolveLabelSource(
  connectionId: string,
  objectName: string,
): Promise<LabelSource | null> {
  try {
    const entities = await loadEntities(connectionId)
    const all = [...(entities.catalogs ?? []), ...(entities.documents ?? [])]
    const hit = all.find((e) => e?.name === objectName)
    if (hit) return hit
  } catch {
    // Registry unreachable — the config fallback may still answer.
  }
  return loadObjectConfig(connectionId, objectName)
}

export function _resetObjectLabelCacheForTests(): void {
  configCache.clear()
  configInFlight.clear()
  entitiesInFlight.clear()
}

export function useObjectLabel(
  connectionId: string | undefined,
  objectName: string,
  locale: string,
  defaultLocale?: string,
): string {
  const [label, setLabel] = useState(objectName)

  useEffect(() => {
    setLabel(objectName)
    if (!connectionId || !objectName) return
    let cancelled = false
    resolveLabelSource(connectionId, objectName)
      .then((source) => {
        if (cancelled || !source) return
        setLabel(
          resolveLabel(
            { synonyms: source.synonyms, synonym: source.synonym, name: source.name || objectName },
            locale,
            defaultLocale,
          ),
        )
      })
      .catch(() => {
        /* keep the raw name */
      })
    return () => {
      cancelled = true
    }
  }, [connectionId, objectName, locale, defaultLocale])

  return label
}
